import React, {useState} from 'react';
import {
  Box, Typography, Container, Paper, Divider,
  Button, Snackbar, Alert, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Grid, Card,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import Iconify from 'packages/common/components/iconify';

export default function App() {
  const theme = useTheme();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');

  // Code d'exemple pour les réservations en mode flottant
  const reservationFloatingCode = `<div id="brydge-widget"></div>
<script
    src="http://widgets.brydge.app/loader.js"
    data-company-id="VOTRE_COMPANY_ID"
    data-api-key="VOTRE_API_KEY"
    data-widget-type="reservations"
    data-mode="floating"
    data-button-text="Réserver une table"
    data-primary-color="#4C9AFF"
></script>`;

  // Code d'exemple pour les réservations en mode fixe
  const reservationFixedCode = `<div id="brydge-widget"></div>
<script
    src="http://widgets.brydge.app/loader.js"
    data-company-id="VOTRE_COMPANY_ID"
    data-api-key="VOTRE_API_KEY"
    data-widget-type="reservations"
    data-mode="fixed"
    data-max-width="600px"
    data-centered="true"
></script>`;

  // Code d'exemple pour les bons cadeaux
  const giftVoucherCode = `<div id="brydge-widget"></div>
<script
    src="http://widgets.brydge.app/loader.js"
    data-company-id="VOTRE_COMPANY_ID"
    data-api-key="VOTRE_API_KEY"
    data-widget-type="giftvouchers"
    data-mode="floating"
    data-button-text="Acheter un bon cadeau"
></script>`;

  const handleCopyCode = (code, description) => {
    navigator.clipboard.writeText(code)
        .then(() => {
          setCopiedCode(description);
          setSnackbarOpen(true);
        });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Configuration des paramètres disponibles
  const parameters = [
    {
      name: 'data-company-id',
      required: true,
      description: 'ID unique de votre entreprise',
      example: '"680b7f0ead9d27275def5335"',
    },
    {
      name: 'data-api-key',
      required: true,
      description: 'Clé API publique de votre entreprise',
      example: '"pub_32be1ba09018077789b0cc8650daa0b3"',
    },
    {
      name: 'data-widget-type',
      required: false,
      description: 'Type de widget à afficher',
      example: '"reservations" | "giftvouchers"',
      default: 'reservations',
    },
    {
      name: 'data-mode',
      required: false,
      description: 'Mode d\'affichage du widget',
      example: '"fixed" | "floating"',
      default: 'fixed',
    },
    {
      name: 'data-button-text',
      required: false,
      description: 'Texte du bouton (mode flottant uniquement)',
      example: '"Réserver une table"',
      default: 'Réserver une table | Acheter un bon cadeau',
    },
    {
      name: 'data-max-width',
      required: false,
      description: 'Largeur maximale (mode fixe)',
      example: '"600px"',
      default: '560px',
    },
    {
      name: 'data-min-height',
      required: false,
      description: 'Hauteur minimale du widget',
      example: '"400px"',
      default: '560px',
    },
    {
      name: 'data-centered',
      required: false,
      description: 'Centrer le widget (mode fixe)',
      example: '"true" | "false"',
      default: 'true',
    },
    {
      name: 'data-primary-color',
      required: false,
      description: 'Couleur principale personnalisée',
      example: '"#4C9AFF"',
      default: 'Réglages Brydge',
    },
    {
      name: 'data-container-id',
      required: false,
      description: 'ID du conteneur spécifique où insérer le widget',
      example: '"mon-conteneur"',
    },
    {
      name: 'data-debug',
      required: false,
      description: 'Activer le mode debug',
      example: '"true" | "false"',
      default: 'false',
    },
  ];

  const widgetTypes = [
    {
      type: 'reservations',
      name: 'Réservations de table',
      icon: <RestaurantIcon />,
      variations: ['reservation', 'reservations', 'booking', 'bookings', 'table', 'tables'],
      defaultButton: 'Réserver une table',
      color: '#4C9AFF',
    },
    {
      type: 'giftvouchers',
      name: 'Bons cadeaux',
      icon: <CardGiftcardIcon />,
      variations: ['giftvoucher', 'giftvouchers', 'giftcard', 'giftcards', 'voucher', 'vouchers'],
      defaultButton: 'Acheter un bon cadeau',
      color: '#FF6B6B',
    },
  ];

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            width: '100%',
            maxWidth: 1000,
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Brydge Widgets - Documentation
          </Typography>
          <Typography variant="subtitle1">
            Environnement: <strong>{process.env.REACT_APP_ENV || 'development'}</strong>
          </Typography>
          <Typography variant="body2" sx={{mt: 1}}>
            Intégrez facilement nos widgets sur votre site web
          </Typography>
        </Paper>

        <Grid container spacing={6} sx={{width: '100%', maxWidth: 1000, mb: 3}}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
                Widgets disponibles
            </Typography>
            <Divider/>
          </Grid>

          <Grid item container spacing={2}>
            {widgetTypes.map((widget) => (
              <Grid item xs={12} md={6} key={widget.type}>
                <Card
                  variant='outlined'
                  sx={{
                    p: 3,
                    borderRadius: 4,
                  }}
                >
                  <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                    <Box sx={{color: widget.color, mr: 1}}>
                      {widget.icon}
                    </Box>
                    <Typography variant="h6">{widget.name}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                      Type: <code>{widget.type}</code>
                  </Typography>
                  <Typography variant="caption" display="block">
                      Variations acceptées: {widget.variations.join(', ')}
                  </Typography>
                  <Box sx={{mt: 1}}>
                    <Button
                      href={`/${widget.type}?companyId=680b7f0ead9d27275def5335&apiKey=pub_32be1ba09018077789b0cc8650daa0b3`}
                      target="_blank"
                      sx={{fontSize: '0.875rem'}}
                    >
                        Voir la démo →
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Exemples d'intégration */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
                Exemples d&apos;intégration
            </Typography>
            <Divider sx={{mb: 2}} />

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Widget de réservation - Mode flottant</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Le widget apparaît comme un bouton flottant en bas à droite de la page
                </Typography>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                  <Typography variant="subtitle2">Code d&apos;intégration:</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopyCode(reservationFloatingCode, 'Réservation flottante')}
                  >
                      Copier
                  </Button>
                </Box>
                <Paper sx={{p: 2, bgcolor: 'grey.100', borderRadius: 1}}>
                  <Typography variant="body2" component="pre" sx={{fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap'}}>
                    {reservationFloatingCode}
                  </Typography>
                </Paper>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Widget de réservation - Mode fixe</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Le widget s&apos;intègre directement dans votre page à l&apos;endroit choisi
                </Typography>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                  <Typography variant="subtitle2">Code d&apos;intégration:</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopyCode(reservationFixedCode, 'Réservation fixe')}
                  >
                      Copier
                  </Button>
                </Box>
                <Paper sx={{p: 2, bgcolor: 'grey.100', borderRadius: 1}}>
                  <Typography variant="body2" component="pre" sx={{fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap'}}>
                    {reservationFixedCode}
                  </Typography>
                </Paper>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Widget bons cadeaux</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Permet à vos clients d&apos;acheter des bons cadeaux
                </Typography>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                  <Typography variant="subtitle2">Code d&apos;intégration:</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopyCode(giftVoucherCode, 'Bons cadeaux')}
                  >
                      Copier
                  </Button>
                </Box>
                <Paper sx={{p: 2, bgcolor: 'grey.100', borderRadius: 1}}>
                  <Typography variant="body2" component="pre" sx={{fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap'}}>
                    {giftVoucherCode}
                  </Typography>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Documentation des paramètres */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
                Paramètres disponibles
            </Typography>
            <Divider />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Paramètre</strong></TableCell>
                    <TableCell><strong>Requis</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Exemple/Valeurs</strong></TableCell>
                    <TableCell><strong>Défaut</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parameters.map((param) => (
                    <TableRow key={param.name}>
                      <TableCell>
                        <code style={{fontSize: '0.8rem'}}>{param.name}</code>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={param.required ? 'Oui' : 'Non'}
                          color={param.required ? 'error' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{param.description}</TableCell>
                      <TableCell>
                        <code style={{fontSize: '0.75rem', color: theme.palette.text.secondary}}>
                          {param.example}
                        </code>
                      </TableCell>
                      <TableCell>
                        {param.default && (
                          <code style={{fontSize: '0.75rem'}}>{param.default}</code>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert color='warning' icon={<Iconify icon="mdi:warning" />} sx={{mt: 3, p: 2, borderRadius: 1}}>
              <Typography variant="subtitle1" gutterBottom>
                Notes importantes:
              </Typography>
              <Typography variant="body2" component="div">
                <ul style={{margin: 0, paddingLeft: '1.2rem'}}>
                  <li>Le <code>data-company-id</code> et <code>data-api-key</code> sont obligatoires</li>
                  <li>Le mode &quot;floating&quot; affiche un bouton flottant, &quot;fixed&quot; intègre directement le widget</li>
                  <li>Si aucune couleur n&apos;est spécifiée, la couleur de votre entreprise sera utilisée automatiquement</li>
                  <li>Le widget se redimensionne automatiquement selon son contenu</li>
                </ul>
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{width: '100%'}}>
          Code d&apos;intégration &quot;{copiedCode}&quot; copié !
        </Alert>
      </Snackbar>
    </Container>
  );
}
